'use client';
import type { ReactNode } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { eventDrawHref, eventPlayerHref } from '../../libs/eventPlayers';
import { NAME_WRAP } from '../../libs/nameBreaks';
import { displayMatchCode } from '../../libs/matchCode';
import type { LoadedPlayerJourney } from '../../libs/loadPlayerJourney';
import type { FinishRange } from '../../libs/playerFinishRange';
import { type JourneyContext } from './JourneyFixtureCard';
import { PlayerJourneyRun } from './PlayerJourneyRun';
import { PlayerJourneyPool } from './PlayerJourneyPool';
import { PlayerDrawPath } from './PlayerDrawPath';
import { JourneyShareButton } from './JourneyShareButton';
import { NameText } from './NameText';
import { useJourneyRefresh } from './useJourneyRefresh';

/** Accepts safe public projections only. `action` is reserved for the shared follow control. */
export function EventPlayerJourney({
  data,
  action,
  ...context
}: JourneyContext & { data: LoadedPlayerJourney; action?: ReactNode }) {
  const t = useTranslations('Event.playerJourney');
  const router = useRouter();
  const { player, journey, division } = data;
  useJourneyRefresh(context.eventId, !!journey && !data.competitionHidden);
  const href = `/${context.locale}${eventPlayerHref(context.eventId, context.slug, player.playerId, division.id)}`;
  const drawHref = (drawId: string) =>
    `/${context.locale}${eventDrawHref(context.eventId, context.slug, drawId)}`;
  const range = (r: FinishRange) =>
    r.best === r.worst
      ? t('place', { place: r.best })
      : t('range', { best: r.best, worst: r.worst });
  return (
    <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,17.5rem)_minmax(0,1fr)] lg:items-start">
      <div className="min-w-0 space-y-4">
        <header className="bg-card border-border rounded-xl border p-4">
          {/* Avatar and follow share the top row so the name below gets the card's full width. */}
          <div data-player-identity className="flex items-center gap-3">
            <Avatar name={player.label} photoUrl={player.photo} size="lg" />
            <p className="text-muted min-w-0 flex-1 text-xs">{t('eventPlayer')}</p>
            {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
          </div>
          <div className="mt-3 min-w-0">
            <h1
              className={`font-heading text-foreground text-2xl font-bold lg:text-xl ${NAME_WRAP}`}
            >
              <NameText name={player.label} />
            </h1>
            {player.members.length > 0 && (
              <p className={`text-muted text-sm ${NAME_WRAP}`}>{player.members.join(' · ')}</p>
            )}
            {(player.country || player.rating != null) && (
              <div className="mt-1 flex flex-wrap gap-2">
                {player.country && <Badge variant="outline">{player.country.iso3}</Badge>}
                {player.rating != null && (
                  <Badge variant="outline">{t('rating', { rating: player.rating })}</Badge>
                )}
              </div>
            )}
          </div>
          <nav
            className="border-border mt-4 flex w-full flex-wrap items-center justify-between gap-2 border-t pt-2"
            aria-label={t('more')}
          >
            <JourneyShareButton href={href} />
            {player.profileUsername && (
              <Link
                className="text-muted inline-flex min-h-11 items-center text-sm underline underline-offset-4"
                href={`/${context.locale}/user/${encodeURIComponent(player.profileUsername)}`}
              >
                {t('profile')}
              </Link>
            )}
          </nav>
        </header>
        {data.divisions.length > 1 ? (
          <div className="max-w-sm">
            <Select
              label={t('division')}
              value={division.id}
              onChange={(e) =>
                router.push(
                  `/${context.locale}${eventPlayerHref(context.eventId, context.slug, player.playerId, e.target.value)}`,
                )
              }
            >
              {data.divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <p className="text-foreground font-semibold">{division.name}</p>
        )}
        {journey && !journey.pool && (
          <section aria-label={t('standing')}>
            {/* The whole card goes back to the division's draw; the trailing cue says so. */}
            <Link
              href={drawHref(journey.drawId)}
              className="group bg-card border-border hover:border-primary hover:bg-surface focus-visible:ring-primary focus-visible:ring-offset-background block rounded-xl border p-4 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              <p className="text-muted text-xs">{t('standing')}</p>
              <p className="font-heading mt-1 font-semibold">
                {journey.next
                  ? [displayMatchCode(journey.next.drawMatchId), journey.next.stageName, journey.next.roundName]
                      .filter(Boolean)
                      .join(' · ')
                  : t(journey.state)}
              </p>
              {journey.standing && (
                <p className="text-muted mt-2 text-xs">
                  {t(journey.standing.final ? 'poolStandingFinal' : 'poolStanding', {
                    stage: journey.standing.stageName,
                    position: journey.standing.position,
                    wins: journey.standing.wins,
                    losses: journey.standing.losses,
                    points: journey.standing.points,
                  })}
                </p>
              )}
              <p className="text-muted mt-1 text-xs">
                {journey.state === 'finished' && journey.finish
                  ? range(journey.finish)
                  : t(journey.state === 'awaiting' ? 'resultPending' : 'placementUnknown')}
              </p>
              <span className="border-border text-foreground mt-3 flex min-h-11 items-center justify-between gap-2 border-t pt-3 text-sm font-semibold">
                {t('viewDraw')}
                <ChevronRightIcon
                  aria-hidden="true"
                  className="text-muted group-hover:text-foreground size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
                />
              </span>
            </Link>
          </section>
        )}
        {journey?.pool && (
          <PlayerJourneyPool
            {...context}
            pool={journey.pool}
            opponents={data.opponents}
            playerId={player.playerId}
            division={division.id}
            drawHref={drawHref(journey.drawId)}
          />
        )}
      </div>
      <div className="min-w-0 space-y-4">
        {!journey ? (
          <section className="bg-surface border-border rounded-xl border p-6">
            <h2 className="font-heading text-lg font-semibold">{t('registered')}</h2>
            <p className="text-muted mt-2 text-sm">
              {t(data.competitionHidden ? 'competitionUnavailable' : 'drawPending')}
            </p>
          </section>
        ) : (
          <>
            <PlayerJourneyRun
              {...context}
              journey={journey}
              opponents={data.opponents}
              division={division.id}
            />
            {journey.next?.stageType === 'elimination' && (
              <PlayerDrawPath
                {...context}
                journey={journey}
                player={player}
                opponents={data.opponents}
                division={division.id}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
